import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface EmbroiderySettings {
  outputFormats: string[];
  stitchDensity: 'low' | 'medium' | 'high';
  maxStitchCount: number;
  colorCount: number;
  designDimensions: {
    width: number;
    height: number;
    unit: 'mm' | 'inch';
  };
  threadBrand: string;
  stitchAngle: number;
  addUnderlay: boolean;
  pullCompensation: number;
}

export interface EmbroideryConversion {
  format: string;
  fileData: string;
  fileSize: number;
  stitchCount: number;
  colors: string[];
}

export interface EmbroideryImage {
  id: string;
  file: File;
  preview: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number;
  conversions: EmbroideryConversion[];
  previewSvg?: string;
  errorMessage?: string;
}

export const useEmbroideryConversion = () => {
  const [images, setImages] = useState<EmbroideryImage[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);

  const addImages = useCallback((files: File[]) => {
    const newImages: EmbroideryImage[] = files.map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      file,
      preview: URL.createObjectURL(file),
      status: 'pending',
      progress: 0,
      conversions: []
    }));
    setImages(prev => [...prev, ...newImages]);
  }, []);

  const removeImage = useCallback((id: string) => {
    setImages(prev => {
      const image = prev.find(img => img.id === id);
      if (image?.preview) {
        URL.revokeObjectURL(image.preview);
      }
      return prev.filter(img => img.id !== id);
    });
  }, []);

  const convertImages = useCallback(async (settings: EmbroiderySettings) => {
    if (images.length === 0) {
      toast.error('Please add images to convert');
      return;
    }

    if (settings.outputFormats.length === 0) {
      toast.error('Please select at least one output format');
      return;
    }

    setIsProcessing(true);

    for (const image of images) {
      if (image.status === 'completed') continue;

      setImages(prev => prev.map(img => 
        img.id === image.id 
          ? { ...img, status: 'processing', progress: 0 }
          : img
      ));

      try {
        // Convert image to base64
        const reader = new FileReader();
        const imageBase64 = await new Promise<string>((resolve, reject) => {
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(image.file);
        });

        // Update progress
        setImages(prev => prev.map(img => 
          img.id === image.id ? { ...img, progress: 30 } : img
        ));

        // Convert dimensions to mm if in inches
        const widthMm = settings.designDimensions.unit === 'inch' 
          ? settings.designDimensions.width * 25.4 
          : settings.designDimensions.width;
        const heightMm = settings.designDimensions.unit === 'inch'
          ? settings.designDimensions.height * 25.4
          : settings.designDimensions.height;

        // Call edge function
        const { data, error } = await supabase.functions.invoke('convert-to-embroidery', {
          body: {
            imageBase64,
            outputFormats: settings.outputFormats,
            settings: {
              stitchDensity: settings.stitchDensity,
              maxStitchCount: settings.maxStitchCount,
              colorCount: settings.colorCount,
              designWidth: widthMm,
              designHeight: heightMm,
              threadBrand: settings.threadBrand,
              stitchAngle: settings.stitchAngle,
              addUnderlay: settings.addUnderlay,
              pullCompensation: settings.pullCompensation
            }
          }
        });

        if (error) throw error;

        setImages(prev => prev.map(img => 
          img.id === image.id 
            ? { 
                ...img, 
                status: 'completed',
                progress: 100,
                conversions: data.conversions,
                previewSvg: data.preview.svgData
              }
            : img
        ));

        toast.success(`Converted ${image.file.name} to ${settings.outputFormats.length} format(s)`);

      } catch (error) {
        console.error('Conversion error:', error);
        setImages(prev => prev.map(img => 
          img.id === image.id 
            ? { 
                ...img, 
                status: 'failed',
                progress: 0,
                errorMessage: error.message || 'Conversion failed'
              }
            : img
        ));
        toast.error(`Failed to convert ${image.file.name}`);
      }
    }

    setIsProcessing(false);
  }, [images]);

  const downloadConversion = useCallback((image: EmbroideryImage, format: string) => {
    const conversion = image.conversions.find(c => c.format === format);
    if (!conversion) return;

    const blob = new Blob([atob(conversion.fileData)], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${image.file.name.split('.')[0]}.${format.toLowerCase()}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, []);

  const downloadAllFormats = useCallback(async (image: EmbroideryImage) => {
    const JSZip = (await import('jszip')).default;
    const zip = new JSZip();
    const folderName = image.file.name.split('.')[0];
    const folder = zip.folder(folderName);

    image.conversions.forEach(conversion => {
      folder?.file(
        `${folderName}.${conversion.format.toLowerCase()}`,
        atob(conversion.fileData),
        { binary: true }
      );
    });

    const blob = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${folderName}-embroidery.zip`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, []);

  const downloadAll = useCallback(async () => {
    const completedImages = images.filter(img => img.status === 'completed');
    if (completedImages.length === 0) return;

    const JSZip = (await import('jszip')).default;
    const zip = new JSZip();

    completedImages.forEach(image => {
      const folderName = image.file.name.split('.')[0];
      const folder = zip.folder(folderName);

      image.conversions.forEach(conversion => {
        folder?.file(
          `${folderName}.${conversion.format.toLowerCase()}`,
          atob(conversion.fileData),
          { binary: true }
        );
      });
    });

    const blob = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'embroidery-conversions.zip';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Downloaded all conversions');
  }, [images]);

  const clearAll = useCallback(() => {
    images.forEach(img => {
      if (img.preview) URL.revokeObjectURL(img.preview);
    });
    setImages([]);
  }, [images]);

  return {
    images,
    isProcessing,
    addImages,
    removeImage,
    convertImages,
    downloadConversion,
    downloadAllFormats,
    downloadAll,
    clearAll
  };
};
